import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

export default function EditarProducto({
  initial,
  onClose,
  onSaved,
}) {
  // --- ESTADOS DE CAMPOS ---
  const id = useMemo(() => initial?.id ?? null, [initial]);
  const [codigo, setCodigo] = useState(initial?.codigo || "");
  const [nombre, setNombre] = useState(initial?.nombre || "");
  const [codigoSap, setCodigoSap] = useState(initial?.codigoSap || "");
 const [estadoId, setEstadoId] = useState(initial?.estado || "");
  const [ivaId, setIvaId] = useState(initial?.ivaId || "");
  const [rubroId, setRubroId] = useState(initial?.rubroId || "");
  const [unidadMedidaId, setUnidadMedidaId] = useState(initial?.unidadMedidaId || "");

  // --- ESTADOS DE CATÁLOGOS ---
  const [listas, setListas] = useState({
    estados: [],
    ivas: [],
    rubros: [],
    unidades: [],
  });

  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState("");

  const isValid =
    codigo.trim() !== "" &&
    nombre.trim() !== "" &&
    codigoSap.trim() !== "" &&
    estadoId !== "" &&
    ivaId !== "" &&
    rubroId !== "" &&
    unidadMedidaId !== "";

  // ESC para cerrar
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Cargar Combos al iniciar
  useEffect(() => {
    const cargarCombos = async () => {
      try {
        const [resEst, resIva, resRub, resUni] = await Promise.all([
          appFetchJson(apiRoutes.estado.listUrl({ page: 0, size: 200, tipoId: 3 })),
          appFetchJson(apiRoutes.ivas.listUrl({ page: 0, size: 200 })),
          appFetchJson(apiRoutes.rubros.listUrl({ page: 0, size: 200 })),
          appFetchJson(apiRoutes.unidadesMedida.listUrl({ page: 0, size: 200 })),
        ]);

        setListas({
          estados: resEst?.items || [],
          ivas: resIva?.items || [],
          rubros: resRub?.items || [],
          unidades: resUni?.items || [],
        });
      } catch (e) {
        setError("Error al cargar los catálogos.");
      } finally {
        setLoadingData(false);
      }
    };
    cargarCombos();
  }, []);

  // Detectar si hay cambios para habilitar botón guardar
  const changed = useMemo(() => {
    return (
      codigo !== initial?.codigo ||
      nombre !== initial?.nombre ||
      codigoSap !== initial?.codigoSap ||
      String(estadoId) !== String(initial?.estadoId) ||
      String(ivaId) !== String(initial?.ivaId) ||
      String(rubroId) !== String(initial?.rubroId) ||
      String(unidadMedidaId) !== String(initial?.unidadMedidaId)
    );
  }, [codigo, nombre, codigoSap, estadoId, ivaId, rubroId, unidadMedidaId, initial]);

const handleSave = async () => {
  // Solo procede si el ID existe, hay cambios y los datos son válidos
  if (!id || !changed || !isValid || saving) return;
    setError("");
    setSaving(true);

    try {
      const payload = {
        id: id,
        codigo: codigo.trim(),
        nombre: nombre.trim(),
        codigoSap: codigoSap.trim(),
        // Aseguramos que codigoPetropar se envíe del objeto inicial si no está en el form
        codigoPetropar: initial?.codigoPetropar || "", 
        estadoId: Number(estadoId),
        ivaId: Number(ivaId),
        rubroId: Number(rubroId),
        unidadMedidaId: Number(unidadMedidaId),
        // IMPORTANTE: Mapear los nombres exactos que ProductoRequestDto espera
        precioPorton: initial?.precioPorton ?? 0,
        precioCompra: initial?.precioCompra ?? 0,
        margenPetropar: initial?.margenPetropar ?? 0,
      };

      const updated = await appFetchJson(apiRoutes.productos.update(id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      onSaved(updated);
      onClose();
    } catch (e) {
      setError(e?.message || "Error al actualizar el producto.");
    } finally {
      setSaving(false);
    }
  };

  if (loadingData) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Editar Producto</h2>
          <button className="modal-close-x" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="modal-body">
          {error && <div className="text-red-600 mb-3 font-medium">{error}</div>}

          <div className="form-grid">
            <Fila label="Cód. Producto" input={
              <input className="input" value={codigo} onChange={(e) => setCodigo(e.target.value)} />
            }/>
            <Fila label="Descripción" input={
              <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} />
            }/>
            <Fila label="Cod. Sap" input={
              <input className="input" value={codigoSap} onChange={(e) => setCodigoSap(e.target.value)} />
            }/>

            <Fila label="Estado" input={
              <select className="input" value={estadoId} onChange={(e) => setEstadoId(e.target.value)}>
                {listas.estados.map(opt => <option key={opt.id} value={opt.id}>{opt.nombre}</option>)}
              </select>
            }/>

            <Fila label="Iva" input={
              <select className="input" value={ivaId} onChange={(e) => setIvaId(e.target.value)}>
                {listas.ivas.map(opt => <option key={opt.id} value={opt.id}>{opt.nombre}</option>)}
              </select>
            }/>

            <Fila label="Rubro" input={
              <select className="input" value={rubroId} onChange={(e) => setRubroId(e.target.value)}>
                {listas.rubros.map(opt => <option key={opt.id} value={opt.id}>{opt.nombre}</option>)}
              </select>
            }/>

            <Fila label="Unidad de Medida" input={
              <select className="input" value={unidadMedidaId} onChange={(e) => setUnidadMedidaId(e.target.value)}>
                {listas.unidades.map(opt => <option key={opt.id} value={opt.id}>{opt.nombre}</option>)}
              </select>
            }/>
          </div>
        </div>

        <div className="modal-footer">
  <div className="btn-row">
    <BtnGuardar 
      label="Guardar"
      isValid={isValid && changed} // Solo activo si es válido Y hay cambios
      saving={saving}
      onClick={handleSave}
    />
    <BtnCancelar onClick={onClose} />
  </div>
</div>
      </div>
    </div>
  );
}

function Fila({ label, input }) {
  return (
    <div className="form-row">
      <label className="form-label">{label}:</label>
      {input}
    </div>
  );
}